import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Button,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import {
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  DescriptionOutlined as FileIcon,
  FolderOutlined as FolderIcon,
  CloudUploadOutlined as UploadIcon,
  DeleteOutlined as DeleteIcon,
  SyncOutlined as SyncIcon,
  EditOutlined as EditIcon,
  MoveToInboxOutlined as MoveIcon,
} from "@mui/icons-material";
import { getActivityLogs } from "../services/logsService";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const ACTIVITY_TYPES = [
  { value: "FILE_UPLOAD", label: "Upload de Arquivo" },
  { value: "FILE_DELETE", label: "Exclusão de Arquivo" },
  { value: "FILE_UPDATE", label: "Atualização de Arquivo" },
  { value: "FOLDER_CREATE", label: "Criação de Pasta" },
  { value: "FOLDER_DELETE", label: "Exclusão de Pasta" },
  { value: "FOLDER_UPDATE", label: "Atualização de Pasta" },
  { value: "FOLDER_MOVE", label: "Movimentação de Pasta" },
  { value: "VERSION_CREATE", label: "Nova Versão de Arquivo" },
];

const ENTITY_TYPES = [
  { value: "File", label: "Arquivo" },
  { value: "Folder", label: "Pasta" },
  { value: "Version", label: "Versão" },
];

const getActivityIcon = (action) => {
  switch (action) {
    case "FILE_UPLOAD":
      return <UploadIcon color="primary" />;
    case "FILE_DELETE":
      return <DeleteIcon color="error" />;
    case "FILE_UPDATE":
      return <EditIcon color="info" />;
    case "FOLDER_CREATE":
      return <FolderIcon color="primary" />;
    case "FOLDER_DELETE":
      return <DeleteIcon color="error" />;
    case "FOLDER_UPDATE":
      return <EditIcon color="info" />;
    case "FOLDER_MOVE":
      return <MoveIcon color="warning" />;
    case "VERSION_CREATE":
      return <SyncIcon color="success" />;
    default:
      return <FileIcon />;
  }
};

const getActivityLabel = (action) => {
  return ACTIVITY_TYPES.find((type) => type.value === action)?.label || action;
};

const getEntityIcon = (entityType) => {
  switch (entityType) {
    case "File":
      return <FileIcon />;
    case "Folder":
      return <FolderIcon />;
    case "Version":
      return <SyncIcon />;
    default:
      return <FileIcon />;
  }
};

const getEntityLabel = (entityType) => {
  return (
    ENTITY_TYPES.find((type) => type.value === entityType)?.label || entityType
  );
};

const Monitoring = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 0,
    limit: 10,
    totalPages: 0,
  });

  const [filters, setFilters] = useState({
    action: "",
    entityType: "",
    startDate: "",
    endDate: "",
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadLogs();
  }, [pagination.page, pagination.limit]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const options = {
        action: filters.action,
        entityType: filters.entityType,
        startDate: filters.startDate,
        endDate: filters.endDate,
        page: pagination.page + 1,
        limit: pagination.limit,
      };

      const result = await getActivityLogs(options);
      setLogs(result.logs);
      setPagination({
        ...pagination,
        total: result.pagination.total,
        totalPages: result.pagination.totalPages,
      });
    } catch (error) {
      console.error("Erro ao carregar logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = () => {
    setPagination((prev) => ({
      ...prev,
      page: 0,
    }));
    loadLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      action: "",
      entityType: "",
      startDate: "",
      endDate: "",
    });
    setPagination((prev) => ({
      ...prev,
      page: 0,
    }));
    loadLogs();
  };

  const handleRefresh = () => {
    loadLogs();
  };

  const handleChangePage = (event, newPage) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination((prev) => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const formatTimestamp = (timestamp) => {
    return format(new Date(timestamp), "dd/MM/yyyy HH:mm:ss", { locale: ptBR });
  };

  const formatDetails = (details, action) => {
    try {
      const parsedDetails =
        typeof details === "string" ? JSON.parse(details) : details;

      switch (action) {
        case "FILE_UPLOAD":
          return `Arquivo: ${parsedDetails.name} (${(
            parsedDetails.size / 1024
          ).toFixed(2)} KB)`;
        case "FILE_DELETE":
          return `Arquivo: ${parsedDetails.name}`;
        case "FILE_UPDATE":
          if (parsedDetails.previousName !== parsedDetails.name) {
            return `Arquivo renomeado de "${parsedDetails.previousName}" para "${parsedDetails.name}"`;
          }
          if (parsedDetails.previousFolderId !== parsedDetails.folderId) {
            return `Arquivo movido para outra pasta`;
          }
          return `Arquivo ${parsedDetails.name} atualizado`;
        case "FOLDER_CREATE":
          return `Pasta: ${parsedDetails.name}`;
        case "FOLDER_DELETE":
          return `Pasta: ${parsedDetails.name}`;
        case "FOLDER_UPDATE":
          if (parsedDetails.previousName !== parsedDetails.name) {
            return `Pasta renomeada de "${parsedDetails.previousName}" para "${parsedDetails.name}"`;
          }
          return `Pasta ${parsedDetails.name} atualizada`;
        case "FOLDER_MOVE":
          return `Pasta ${parsedDetails.name} movida de "${parsedDetails.previousPath}" para "${parsedDetails.newPath}"`;
        case "VERSION_CREATE":
          return `Nova versão (${parsedDetails.versionNumber}) do arquivo ${parsedDetails.fileName}`;
        default:
          return JSON.stringify(parsedDetails);
      }
    } catch (e) {
      return details;
    }
  };

  return (
    <Box>
      <Box
        mb={4}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Monitoramento de Atividades
        </Typography>
        <Box>
          <Tooltip title="Atualizar">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filtros">
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              color={showFilters ? "primary" : "default"}
            >
              <FilterIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {showFilters && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="action-label">Tipo de Ação</InputLabel>
                  <Select
                    labelId="action-label"
                    id="action"
                    name="action"
                    value={filters.action}
                    onChange={handleFilterChange}
                    label="Tipo de Ação"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {ACTIVITY_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="entity-type-label">
                    Tipo de Entidade
                  </InputLabel>
                  <Select
                    labelId="entity-type-label"
                    id="entityType"
                    name="entityType"
                    value={filters.entityType}
                    onChange={handleFilterChange}
                    label="Tipo de Entidade"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {ENTITY_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  id="startDate"
                  name="startDate"
                  label="Data Inicial"
                  type="datetime-local"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  id="endDate"
                  name="endDate"
                  label="Data Final"
                  type="datetime-local"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{ mr: 1 }}
                >
                  Limpar
                </Button>
                <Button variant="contained" onClick={handleApplyFilters}>
                  Aplicar Filtros
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Paper elevation={2}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ação</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Detalhes</TableCell>
                <TableCell>Caminho</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Data e Hora</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" py={3}>
                      Nenhum registro de atividade encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getActivityIcon(log.action)}
                        <Typography sx={{ ml: 1 }}>
                          {getActivityLabel(log.action)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getEntityIcon(log.entityType)}
                        label={getEntityLabel(log.entityType)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {formatDetails(log.details, log.action)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={log.entityPath}>
                        <Typography
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {log.entityPath}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{log.user?.name}</TableCell>
                    <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={handleChangePage}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
        />
      </Paper>
    </Box>
  );
};

export default Monitoring;
